import React, { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, X } from "lucide-react";
import { toast } from "sonner";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Badge } from "~/components/ui/badge";
import { CopyButton } from "~/components/ui/copy-button";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/components/ui/alert-dialog";
import { Skeleton } from "~/components/ui/skeleton";
import {
  SelectionCombobox,
  SelectionComboboxOption,
} from "~/components/ui/selection-combobox";
import { calculateDammChecksum } from "~/util/Damm";
import { Routes, getRoute } from "~/routes";
import {
  useErUsLazyQuery,
  useCassettesLazyQuery,
  useGetCustomerCassettesLazyQuery,
  useGetCustomerErusLazyQuery,
  useGetCustomerOrganisationsLazyQuery,
  useOrganisationsLazyQuery,
  useUpdateCustomerMutation,
} from "generated/hooks";
import { useFetchAll } from "~/hooks/useFetchAll";
import {
  IndicatorLimitDefinition,
  IndicatorLimitSaveInput,
  IndicatorLimitSection,
} from "./IndicatorLimitSection";



interface AssignmentItem {
  id: string;
  label: string;
  avatarUrl?: string | null;
  showAvatar?: boolean;
  href?: string;
  /** When set, a copy button is rendered on the chip for this value. */
  copyValue?: string;
  /** Noun used in the copy button's tooltip and accessible name. */
  copyLabel?: string;
}

interface AssignmentSectionProps {
  title: string;
  options: SelectionComboboxOption[];
  onAdd: (value: string) => void;
  addPlaceholder: string;
  emptyOptionsMessage: string;
  assignedItems: AssignmentItem[];
  onRemove: (id: string) => void;
  emptyAssignedMessage: string;
  loadingOptions?: boolean;
  disabled?: boolean;
}

const AssignmentSection: React.FC<AssignmentSectionProps> = (props) => {
  const {
    title,
    options,
    onAdd,
    addPlaceholder,
    emptyOptionsMessage,
    assignedItems,
    onRemove,
    emptyAssignedMessage,
    loadingOptions,
    disabled,
  } = props;

  const isLoadingOptions = Boolean(loadingOptions);
  const shouldShowCombobox = isLoadingOptions || options.length > 0;
  const comboboxPlaceholder = isLoadingOptions
    ? `Loading ${title}...`
    : addPlaceholder;
  const comboboxEmptyMessage = isLoadingOptions ? "Loading..." : "No results";
  const isComboboxDisabled = disabled || isLoadingOptions;

  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">
          Manage the {title.toLowerCase()} associated with this customer.
        </p>
      </header>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="w-full max-w-xs sm:max-w-sm">
          {shouldShowCombobox ? (
            <SelectionCombobox
              placeholder={comboboxPlaceholder}
              options={options}
              emptyMessage={comboboxEmptyMessage}
              onSelect={onAdd}
              loading={loadingOptions}
              disabled={isComboboxDisabled}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {emptyOptionsMessage}
            </p>
          )}
        </div>
        <div className="flex w-full flex-wrap gap-2">
          {assignedItems.length > 0 ? (
            assignedItems.map((item) => {
              const fallbackInitial =
                item.label.trim().charAt(0).toUpperCase() || "?";
              return (
                <Badge
                  key={item.id}
                  variant="secondary"
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-secondary/40 px-2.5 py-0.5 text-[11px] font-medium text-foreground"
                >
                  {item.showAvatar ? (
                    <Avatar className="size-4 border border-border/70">
                      <AvatarImage
                        src={item.avatarUrl ?? undefined}
                        alt={item.label}
                      />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700 text-[11px] font-semibold">
                        {fallbackInitial}
                      </AvatarFallback>
                    </Avatar>
                  ) : null}
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="max-w-[200px] truncate underline-offset-4 hover:underline"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span className="max-w-[200px] truncate">{item.label}</span>
                  )}
                  {item.copyValue ? (
                    <CopyButton
                      value={item.copyValue}
                      label={item.copyLabel ?? "value"}
                    />
                  ) : null}
                  <button
                    type="button"
                    className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => onRemove(item.id)}
                    aria-label={`Remove ${item.label}`}
                  >
                    <X className="size-3" aria-hidden="true" />
                  </button>
                </Badge>
              );
            })
          ) : (
            <p className="text-sm text-muted-foreground">
              {emptyAssignedMessage}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export interface CustomerAccordionProps {
  items: Array<{
    id: string;
    title: string;
    subtitle?: string;
    stats?: Array<{ label: string; value: number; className?: string }>;
    children?: ReactNode;
  }>;
}

interface CassetteSerialNumberData {
  serialNumber: number;
  range: {
    productType: {
      code: string;
    };
  };
}

interface CustomerCassette {
  id: string;
  remainingCustomerTests?: number | null;
  remainingPhysicalSensors?: number | null;
  hasOpenOverflowWarning?: boolean | null;
  serialNumber?: CassetteSerialNumberData | null;
}

export interface CustomerAccordionContentProps {
  customerId: string;
  customerName?: string;
  customerSince?: string;
  description?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  notes?: string | null;
  limitIndicators?: boolean;
  allowedIndicatorDefinitions?: IndicatorLimitDefinition[];
  organisations?: Array<{ id: string; name: string; logoUrl?: string | null }>;
  organisationsPageInfo?: { hasNextPage: boolean; endCursor?: string | null };
  erus?: Array<{ id: string; name: string }>;
  erusPageInfo?: { hasNextPage: boolean; endCursor?: string | null };
  cassettes?: Array<CustomerCassette>;
  cassettesPageInfo?: { hasNextPage: boolean; endCursor?: string | null };
  loading?: boolean;
  onItemsChange?: (
    type: "organisations" | "erus" | "cassettes",
    items: Array<
      | { id: string; name: string; logoUrl?: string | null }
      | { id: string; name: string }
      | CustomerCassette
    >
  ) => void;
  onTriggerConfirmDelete?: () => void;
  deleteConfirmationTitle?: string;
  deleteConfirmationMessage?: string;
}

/**
 * The cassette carrying the richest data we have: the cache entry when one has
 * been seen, otherwise the cassette as given.
 */
const resolveCassetteWithBestData = (
  cassette: CustomerCassette,
  cache: Map<string, CustomerCassette>
): CustomerCassette =>
  (cassette?.id ? cache.get(cassette.id) : undefined) ?? cassette;

/**
 * The single source of truth for the printed identifier format. A product-type
 * prefix carries a Damm checksum; without one there is only the padded serial.
 */
const formatSerialNumber = (
  serialNumber: number,
  productCode?: string | null
): string => {
  const serial = serialNumber.toString().padStart(7, "0");

  return productCode
    ? `${productCode}${serial}${calculateDammChecksum(`${productCode}${serial}`)}`
    : serial;
};

/**
 * The printed serial number, or null when the cassette has not been assigned
 * one. Unlike the display label this never falls back to an internal id, so it
 * is safe to place on the clipboard.
 */
const getCassetteSerialNumber = (
  cassette: CustomerCassette,
  cache: Map<string, CustomerCassette>
): string | null => {
  const cassetteWithBestData = resolveCassetteWithBestData(cassette, cache);
  const serialNumber = cassetteWithBestData?.serialNumber?.serialNumber;

  if (serialNumber === null || serialNumber === undefined) {
    return null;
  }

  return formatSerialNumber(
    serialNumber,
    cassetteWithBestData.serialNumber?.range?.productType?.code
  );
};

const getFormattedCassetteIdOrFallback = (
  cassette: CustomerCassette,
  cache: Map<string, CustomerCassette>
): string => {
  const cassetteWithBestData = resolveCassetteWithBestData(cassette, cache);
  const serialNumber = cassetteWithBestData?.serialNumber?.serialNumber;

  if (serialNumber === null || serialNumber === undefined) {
    return `Cassette ${cassette?.id?.substring(0, 8) || "N/A"}`;
  }

  const productCode =
    cassetteWithBestData.serialNumber?.range?.productType?.code;
  const formatted = formatSerialNumber(serialNumber, productCode);

  // Bare digits need a label to read as a serial number.
  return productCode ? formatted : `SN ${formatted}`;
};

const getCassetteStatusSuffix = (cassette: CustomerCassette): string => {
  const remainingCustomerTests = cassette.remainingCustomerTests;
  const remainingPhysicalSensors = cassette.remainingPhysicalSensors;

  if (
    remainingCustomerTests === null ||
    remainingCustomerTests === undefined ||
    remainingPhysicalSensors === null ||
    remainingPhysicalSensors === undefined
  ) {
    return cassette.hasOpenOverflowWarning ? " · Overflow warning" : "";
  }

  const suffix = ` · ${remainingCustomerTests} tests left / ${remainingPhysicalSensors} physical`;
  return cassette.hasOpenOverflowWarning ? `${suffix} · Overflow warning` : suffix;
};

const sortSelectionComboboxOptionsByLabel = (
  a: SelectionComboboxOption,
  b: SelectionComboboxOption
) =>
  a.label.localeCompare(b.label, undefined, {
    sensitivity: "base",
    numeric: true,
  });

const dedupeSelectionOptions = (options: SelectionComboboxOption[]) =>
  Array.from(new Map(options.map((option) => [option.value, option])).values());

const CustomerAccordionContentSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-10 w-full max-w-sm" />
        <div className="flex flex-wrap gap-3">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-28" />
          <Skeleton className="h-6 w-20" />
        </div>
      </div>
      <div className="space-y-3">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-10 w-full max-w-sm" />
        <Skeleton className="h-6 w-28" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-10 w-full max-w-sm" />
        <Skeleton className="h-6 w-32" />
      </div>
    </div>
  );
};

const mergeById = <T extends { id: string }>(existing: T[], incoming: T[]) => {
  const merged = new Map(existing.map((item) => [item.id, item]));
  incoming.forEach((item) => {
    merged.set(item.id, item);
  });
  return [...merged.values()];
};

export const CustomerAccordionContent: React.FC<
  CustomerAccordionContentProps
> = (props) => {
  const cassetteDataCache = React.useRef(new Map<string, CustomerCassette>());
  const [updateCustomerMutation, { loading: updatingCustomer }] =
    useUpdateCustomerMutation();

  const [localOrganisations, setLocalOrganisations] = useState(
    props.organisations || []
  );
  const [localErus, setLocalErus] = useState(props.erus || []);
  const [localCassettes, setLocalCassettes] = useState<CustomerCassette[]>(
    props.cassettes || []
  );
  const [loadingMoreOrganisations, setLoadingMoreOrganisations] =
    useState(false);
  const [loadingMoreErus, setLoadingMoreErus] = useState(false);
  const [loadingMoreCassettes, setLoadingMoreCassettes] = useState(false);

  const [fetchCustomerOrganisations] = useGetCustomerOrganisationsLazyQuery({
    fetchPolicy: "network-only",
  });
  const [fetchCustomerErus] = useGetCustomerErusLazyQuery({
    fetchPolicy: "network-only",
  });
  const [fetchCustomerCassettes] = useGetCustomerCassettesLazyQuery({
    fetchPolicy: "network-only",
  });

  React.useEffect(() => {
    setLocalOrganisations(props.organisations || []);
    setLocalErus(props.erus || []);

    const incoming = props.cassettes || [];
    incoming.forEach((cassette) => {
      if (cassette.id && cassette.serialNumber) {
        cassetteDataCache.current.set(cassette.id, cassette);
      }
    });
    setLocalCassettes(incoming);
  }, [props.customerId]);

  React.useEffect(() => {
    if (!props.organisationsPageInfo?.hasNextPage) {
      return;
    }

    let isMounted = true;
    const loadRemainingOrganisations = async () => {
      setLoadingMoreOrganisations(true);
      let cursor = props.organisationsPageInfo?.endCursor ?? null;
      const additional: Array<{ id: string; name: string; logoUrl?: string | null }> =
        [];

      try {
        while (cursor) {
          const { data } = await fetchCustomerOrganisations({
            variables: {
              id: props.customerId,
              after: cursor,
            },
          });

          const connection = data?.customer?.organisations;
          const nextNodes =
            connection?.nodes
              ?.filter((node): node is { id: string; name: string; logoUrl?: string | null } => Boolean(node?.id))
              .map((node) => ({
                id: node.id,
                name: node.name || "Unnamed Organization",
                logoUrl: node.logoUrl ?? null,
              })) ?? [];

          additional.push(...nextNodes);

          if (!connection?.pageInfo?.hasNextPage || !connection.pageInfo.endCursor) {
            break;
          }

          cursor = connection.pageInfo.endCursor;
        }

        if (!isMounted || additional.length === 0) {
          return;
        }

        setLocalOrganisations((current) => mergeById(current, additional));
      } catch (error) {
        if (isMounted) {
          toast.error("Failed to load all customer organisations.");
        }
      } finally {
        if (isMounted) {
          setLoadingMoreOrganisations(false);
        }
      }
    };

    void loadRemainingOrganisations();

    return () => {
      isMounted = false;
    };
  }, [
    fetchCustomerOrganisations,
    props.customerId,
    props.organisationsPageInfo?.endCursor,
    props.organisationsPageInfo?.hasNextPage,
  ]);

  React.useEffect(() => {
    if (!props.erusPageInfo?.hasNextPage) {
      return;
    }

    let isMounted = true;
    const loadRemainingErus = async () => {
      setLoadingMoreErus(true);
      let cursor = props.erusPageInfo?.endCursor ?? null;
      const additional: Array<{ id: string; name: string }> = [];

      try {
        while (cursor) {
          const { data } = await fetchCustomerErus({
            variables: {
              id: props.customerId,
              after: cursor,
            },
          });

          const connection = data?.customer?.erus;
          const nextNodes =
            connection?.nodes
              ?.filter((node): node is { id: string; name?: string | null } => Boolean(node?.id))
              .map((node) => ({
                id: node.id,
                name: node.name || "",
              })) ?? [];

          additional.push(...nextNodes);

          if (!connection?.pageInfo?.hasNextPage || !connection.pageInfo.endCursor) {
            break;
          }

          cursor = connection.pageInfo.endCursor;
        }

        if (!isMounted || additional.length === 0) {
          return;
        }

        setLocalErus((current) => mergeById(current, additional));
      } catch (error) {
        if (isMounted) {
          toast.error("Failed to load all customer ERUs.");
        }
      } finally {
        if (isMounted) {
          setLoadingMoreErus(false);
        }
      }
    };

    void loadRemainingErus();

    return () => {
      isMounted = false;
    };
  }, [
    fetchCustomerErus,
    props.customerId,
    props.erusPageInfo?.endCursor,
    props.erusPageInfo?.hasNextPage,
  ]);

  React.useEffect(() => {
    if (!props.cassettesPageInfo?.hasNextPage) {
      return;
    }

    let isMounted = true;
    const loadRemainingCassettes = async () => {
      setLoadingMoreCassettes(true);
      let cursor = props.cassettesPageInfo?.endCursor ?? null;
      const additional: CustomerCassette[] = [];

      try {
        while (cursor) {
          const { data } = await fetchCustomerCassettes({
            variables: {
              id: props.customerId,
              after: cursor,
            },
          });

          const connection = data?.customer?.cassettes;
          const nextNodes =
            connection?.nodes
              ?.filter((node) => Boolean(node?.id))
              .map((node) => ({
                id: node!.id,
                remainingCustomerTests: node.remainingCustomerTests ?? null,
                remainingPhysicalSensors: node.remainingPhysicalSensors ?? null,
                hasOpenOverflowWarning: node.hasOpenOverflowWarning ?? null,
                serialNumber:
                  node?.serialNumber?.range?.productType?.code
                    ? {
                        serialNumber: node.serialNumber.serialNumber,
                        range: {
                          productType: {
                            code: node.serialNumber.range.productType.code,
                          },
                        },
                      }
                    : null,
              })) ?? [];

          nextNodes.forEach((cassette) => {
            if (cassette.id && cassette.serialNumber) {
              cassetteDataCache.current.set(cassette.id, cassette);
            }
          });
          additional.push(...nextNodes);

          if (!connection?.pageInfo?.hasNextPage || !connection.pageInfo.endCursor) {
            break;
          }

          cursor = connection.pageInfo.endCursor;
        }

        if (!isMounted || additional.length === 0) {
          return;
        }

        setLocalCassettes((current) => mergeById(current, additional));
      } catch (error) {
        if (isMounted) {
          toast.error("Failed to load all customer cassettes.");
        }
      } finally {
        if (isMounted) {
          setLoadingMoreCassettes(false);
        }
      }
    };

    void loadRemainingCassettes();

    return () => {
      isMounted = false;
    };
  }, [
    fetchCustomerCassettes,
    props.customerId,
    props.cassettesPageInfo?.endCursor,
    props.cassettesPageInfo?.hasNextPage,
  ]);

  const [fetchOrganisations] = useOrganisationsLazyQuery();
  const [fetchErus] = useErUsLazyQuery();
  const [fetchCassettes] = useCassettesLazyQuery();

  const { data: allOrganisations, loading: loadingOrganisations } = useFetchAll(
    fetchOrganisations,
    (d) => d.organisations,
    { fetchPolicy: "network-only", variables: { after: null } },
  );
  const { data: allErus, loading: loadingErus } = useFetchAll(
    fetchErus,
    (d) => d.erus,
    { fetchPolicy: "network-only", variables: { after: null } },
  );
  const { data: allCassettes, loading: loadingCassettes } = useFetchAll(
    fetchCassettes,
    (d) => d.cassettes,
    { fetchPolicy: "network-only", variables: { after: null } },
  );

  React.useEffect(() => {
    allCassettes.forEach((cassette) => {
      if (!cassette?.id) {
        return;
      }

      cassetteDataCache.current.set(cassette.id, {
        id: cassette.id,
        remainingCustomerTests: cassette.remainingCustomerTests ?? null,
        remainingPhysicalSensors: cassette.remainingPhysicalSensors ?? null,
        hasOpenOverflowWarning: cassette.hasOpenOverflowWarning ?? null,
        serialNumber: cassette.serialNumber?.range?.productType?.code
          ? {
              serialNumber: cassette.serialNumber.serialNumber,
              range: {
                productType: {
                  code: cassette.serialNumber.range.productType.code,
                },
              },
            }
          : null,
      });
    });
  }, [allCassettes]);

  const formatCassetteId = React.useCallback(
    (cassette: CustomerCassette): string =>
      getFormattedCassetteIdOrFallback(cassette, cassetteDataCache.current),
    []
  );

  const formatCassetteSerialNumber = React.useCallback(
    (cassette: CustomerCassette): string | null =>
      getCassetteSerialNumber(cassette, cassetteDataCache.current),
    []
  );

  const organisationOptions = useMemo<SelectionComboboxOption[]>(() => {
    const assignedOrganisationIds = new Set(
      localOrganisations.map((organisation) => organisation.id)
    );

    return dedupeSelectionOptions(
      allOrganisations
        .filter(
          (organisation) =>
            Boolean(organisation?.id) &&
            !assignedOrganisationIds.has(organisation.id)
        )
        .map((organisation) => ({
          label: organisation.name || "Unnamed organisation",
          value: organisation.id,
        }))
        .sort(sortSelectionComboboxOptionsByLabel)
    );
  }, [localOrganisations, allOrganisations]);

  const eruOptions = useMemo<SelectionComboboxOption[]>(() => {
    const assignedEruIds = new Set(localErus.map((eru) => eru.id));

    return dedupeSelectionOptions(
      allErus
        .filter(
          (eru) =>
            Boolean(eru?.id) && !assignedEruIds.has(eru.id)
        )
        .map((eru) => ({
          label: eru.name?.trim() ? eru.name : `ERU ${eru.id.substring(0, 8)}`,
          value: eru.id,
        }))
        .sort(sortSelectionComboboxOptionsByLabel)
    );
  }, [allErus, localErus]);

  const cassetteOptions = useMemo<SelectionComboboxOption[]>(() => {
    const assignedCassetteIds = new Set(localCassettes.map((cassette) => cassette.id));

    const availableCassettes = allCassettes.reduce<CustomerCassette[]>(
      (acc, cassette) => {
        if (!cassette?.id || assignedCassetteIds.has(cassette.id)) {
          return acc;
        }

        acc.push({
          id: cassette.id,
          remainingCustomerTests: cassette.remainingCustomerTests ?? null,
          remainingPhysicalSensors: cassette.remainingPhysicalSensors ?? null,
          hasOpenOverflowWarning: cassette.hasOpenOverflowWarning ?? null,
          serialNumber: cassette.serialNumber?.range?.productType?.code
            ? {
                serialNumber: cassette.serialNumber.serialNumber,
                range: {
                  productType: {
                    code: cassette.serialNumber.range.productType.code,
                  },
                },
              }
            : null,
        });
        return acc;
      },
      [],
    );

    return dedupeSelectionOptions(
      availableCassettes
        .map((cassette) => ({
          label: formatCassetteId(cassette),
          value: cassette.id,
        }))
        .sort(sortSelectionComboboxOptionsByLabel)
    );
  }, [allCassettes, formatCassetteId, localCassettes]);

  const getMutationBaseInput = () => {
    const name = props.customerName?.trim();
    if (!name || !props.customerSince) {
      toast.error("Customer details are unavailable. Please refresh and try again.");
      return null;
    }

    // UpdateCustomerInput replaces every scalar field, so the ones we are not
    // editing must be echoed back or they get wiped by the save.
    return {
      id: props.customerId,
      name,
      customerSince: props.customerSince,
      description: props.description ?? null,
      contactName: props.contactName ?? null,
      contactEmail: props.contactEmail ?? null,
      contactPhone: props.contactPhone ?? null,
      notes: props.notes ?? null,
    };
  };

  const handleSaveIndicatorLimit = async (input: IndicatorLimitSaveInput) => {
    const baseInput = getMutationBaseInput();
    if (!baseInput) {
      throw new Error("Customer details are unavailable.");
    }

    try {
      await updateCustomerMutation({
        variables: {
          input: {
            ...baseInput,
            limitIndicators: input.limitIndicators,
            allowedIndicatorDefinitionIds: input.allowedIndicatorDefinitionIds,
          },
        },
      });
      toast.success(
        input.limitIndicators
          ? "Indicator limit saved."
          : "Indicator limit disabled."
      );
    } catch (error) {
      toast.error(
        `Failed to save indicator limit: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      throw error;
    }
  };

  const handleAddOrganisation = async (organisationId: string) => {
    if (localOrganisations.some((organisation) => organisation.id === organisationId)) {
      return;
    }

    const baseInput = getMutationBaseInput();
    if (!baseInput) {
      return;
    }

    const organisation = allOrganisations.find(
      (node): node is { id: string; name: string; logoUrl?: string | null } =>
        Boolean(node?.id) && node.id === organisationId
    );

    if (!organisation) {
      toast.error("Unable to find the selected organisation.");
      return;
    }

    const nextOrganisations = mergeById(localOrganisations, [
      {
        id: organisation.id,
        name: organisation.name || "Unnamed organisation",
        logoUrl: organisation.logoUrl ?? null,
      },
    ]);

    try {
      await updateCustomerMutation({
        variables: {
          input: {
            ...baseInput,
            organisationIds: nextOrganisations.map((item) => item.id),
          },
        },
      });
      setLocalOrganisations(nextOrganisations);
      props.onItemsChange?.("organisations", nextOrganisations);
      toast.success("Organisation assigned.");
    } catch (error) {
      toast.error(
        `Failed to assign organisation: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleRemoveOrganisation = async (organisationId: string) => {
    const nextOrganisations = localOrganisations.filter(
      (organisation) => organisation.id !== organisationId
    );
    if (nextOrganisations.length === localOrganisations.length) {
      return;
    }

    const baseInput = getMutationBaseInput();
    if (!baseInput) {
      return;
    }

    try {
      await updateCustomerMutation({
        variables: {
          input: {
            ...baseInput,
            organisationIds: nextOrganisations.map((item) => item.id),
          },
        },
      });
      setLocalOrganisations(nextOrganisations);
      props.onItemsChange?.("organisations", nextOrganisations);
      toast.success("Organisation removed.");
    } catch (error) {
      toast.error(
        `Failed to remove organisation: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleAddEru = async (eruId: string) => {
    if (localErus.some((eru) => eru.id === eruId)) {
      return;
    }

    const baseInput = getMutationBaseInput();
    if (!baseInput) {
      return;
    }

    const eru = allErus.find(
      (node): node is { id: string; name?: string | null } =>
        Boolean(node?.id) && node.id === eruId
    );

    if (!eru) {
      toast.error("Unable to find the selected ERU.");
      return;
    }

    const nextErus = mergeById(localErus, [
      {
        id: eru.id,
        name: eru.name || "",
      },
    ]);

    try {
      await updateCustomerMutation({
        variables: {
          input: {
            ...baseInput,
            eruIds: nextErus.map((item) => item.id),
          },
        },
      });
      setLocalErus(nextErus);
      props.onItemsChange?.("erus", nextErus);
      toast.success("ERU assigned.");
    } catch (error) {
      toast.error(
        `Failed to assign ERU: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleRemoveEru = async (eruId: string) => {
    const nextErus = localErus.filter((eru) => eru.id !== eruId);
    if (nextErus.length === localErus.length) {
      return;
    }

    const baseInput = getMutationBaseInput();
    if (!baseInput) {
      return;
    }

    try {
      await updateCustomerMutation({
        variables: {
          input: {
            ...baseInput,
            eruIds: nextErus.map((item) => item.id),
          },
        },
      });
      setLocalErus(nextErus);
      props.onItemsChange?.("erus", nextErus);
      toast.success("ERU removed.");
    } catch (error) {
      toast.error(
        `Failed to remove ERU: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleAddCassette = async (cassetteId: string) => {
    if (localCassettes.some((cassette) => cassette.id === cassetteId)) {
      return;
    }

    const baseInput = getMutationBaseInput();
    if (!baseInput) {
      return;
    }

    const cassette = allCassettes.find(
      (node) => node?.id === cassetteId
    );

    if (!cassette?.id) {
      toast.error("Unable to find the selected cassette.");
      return;
    }

    const cassetteToAdd: CustomerCassette = {
      id: cassette.id,
      remainingCustomerTests: cassette.remainingCustomerTests ?? null,
      remainingPhysicalSensors: cassette.remainingPhysicalSensors ?? null,
      hasOpenOverflowWarning: cassette.hasOpenOverflowWarning ?? null,
      serialNumber: cassette.serialNumber?.range?.productType?.code
        ? {
            serialNumber: cassette.serialNumber.serialNumber,
            range: {
              productType: {
                code: cassette.serialNumber.range.productType.code,
              },
            },
          }
        : null,
    };

    if (cassetteToAdd.serialNumber) {
      cassetteDataCache.current.set(cassetteToAdd.id, cassetteToAdd);
    }

    const nextCassettes = mergeById(localCassettes, [cassetteToAdd]);

    try {
      await updateCustomerMutation({
        variables: {
          input: {
            ...baseInput,
            cassetteIds: nextCassettes.map((item) => item.id),
          },
        },
      });
      setLocalCassettes(nextCassettes);
      props.onItemsChange?.("cassettes", nextCassettes);
      toast.success("Cassette assigned.");
    } catch (error) {
      toast.error(
        `Failed to assign cassette: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleRemoveCassette = async (cassetteId: string) => {
    const nextCassettes = localCassettes.filter(
      (cassette) => cassette.id !== cassetteId
    );
    if (nextCassettes.length === localCassettes.length) {
      return;
    }

    const baseInput = getMutationBaseInput();
    if (!baseInput) {
      return;
    }

    try {
      await updateCustomerMutation({
        variables: {
          input: {
            ...baseInput,
            cassetteIds: nextCassettes.map((item) => item.id),
          },
        },
      });
      setLocalCassettes(nextCassettes);
      props.onItemsChange?.("cassettes", nextCassettes);
      toast.success("Cassette removed.");
    } catch (error) {
      toast.error(
        `Failed to remove cassette: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  if (props.loading) {
    return <CustomerAccordionContentSkeleton />;
  }

  return (
    <div className="space-y-10">
      <AssignmentSection
        title="Organisations"
        options={organisationOptions}
        onAdd={handleAddOrganisation}
        addPlaceholder="Search and select organisation..."
        emptyOptionsMessage="All organisations are already assigned."
        assignedItems={localOrganisations.map((org) => ({
          id: org.id,
          label: org.name || "Unnamed organisation",
          avatarUrl: org.logoUrl ?? undefined,
          showAvatar: true,
        }))}
        onRemove={handleRemoveOrganisation}
        emptyAssignedMessage="No organisations assigned."
        loadingOptions={loadingOrganisations || loadingMoreOrganisations}
        disabled={updatingCustomer}
      />
      <AssignmentSection
        title="ERUs"
        options={eruOptions}
        onAdd={handleAddEru}
        addPlaceholder="Search and select ERU..."
        emptyOptionsMessage="All ERUs are already assigned."
        assignedItems={localErus.map((eru) => ({
          id: eru.id,
          label: eru.name?.trim() ? eru.name : `ERU ${eru.id.substring(0, 8)}`,
          // Only a real name goes on the clipboard, never the `ERU <id
          // prefix>` display fallback.
          copyValue: eru.name?.trim() || undefined,
          copyLabel: "ERU name",
        }))}
        onRemove={handleRemoveEru}
        emptyAssignedMessage="No ERUs assigned."
        loadingOptions={loadingErus || loadingMoreErus}
        disabled={updatingCustomer}
      />
      <AssignmentSection
        title="Cassettes"
        options={cassetteOptions}
        onAdd={handleAddCassette}
        addPlaceholder="Search and select cassette..."
        emptyOptionsMessage="All cassettes are already assigned."
        assignedItems={localCassettes.map((cassette) => ({
          id: cassette.id,
          label: `${formatCassetteId(cassette)}${getCassetteStatusSuffix(
            cassette
          )}`,
          href: getRoute(Routes.AdminCassetteDetails, { id: cassette.id }),
          copyValue: formatCassetteSerialNumber(cassette) ?? undefined,
          copyLabel: "serial number",
        }))}
        onRemove={handleRemoveCassette}
        emptyAssignedMessage="No cassettes assigned."
        loadingOptions={loadingCassettes || loadingMoreCassettes}
        disabled={updatingCustomer}
      />
      <IndicatorLimitSection
        limitIndicators={props.limitIndicators ?? false}
        allowedIndicatorDefinitions={props.allowedIndicatorDefinitions ?? []}
        onSave={handleSaveIndicatorLimit}
        disabled={updatingCustomer}
      />
      {props.customerName !== "PES" ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="destructive"
              size="sm"
              className="flex items-center gap-2"
            >
              <Trash2 className="size-4" aria-hidden="true" />
              Delete customer
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {props.deleteConfirmationTitle || "Delete customer"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {props.deleteConfirmationMessage ||
                  "Are you sure you want to delete this customer? This action cannot be undone and will unassign all related items."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  props.onTriggerConfirmDelete?.();
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  );
};

const CustomerAccordion: React.FC<CustomerAccordionProps> = ({ items }) => {
  return (
    <Accordion type="multiple" className="w-full">
      {items.map((item) => (
        <AccordionItem key={item.id} value={item.id}>
          <AccordionTrigger className="flex-1">
            <div className="flex flex-1 flex-col gap-2 text-left">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <span className="text-sm font-medium text-foreground">
                  {item.title}
                </span>
                {item.stats && item.stats.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {item.stats.map((stat) => (
                      <Badge
                        key={`${item.id}-${stat.label}`}
                        variant="secondary"
                        className={cn(
                          "rounded-full px-3 py-1 text-xs font-medium",
                          stat.className
                        )}
                      >
                        {stat.label}: {stat.value}
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </div>
              {item.subtitle ? (
                <p className="text-sm text-muted-foreground">{item.subtitle}</p>
              ) : null}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-4">{item.children}</div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default CustomerAccordion;
